<template>
  <component :is="asForm ? BaseForm : 'div'" ref="attributeSelectionFormRef" :form="attributeSelection">
    <section
      v-for="({ groupBy, groupedOptions, label, type }, i) in variantAttributes"
      :key="type"
      :class="{ 'mt-6': i !== 0 }"
      :data-scroll-el="`${type}Pickers`"
      :data-test-id="`${type}-groups`"
    >
      <div :class="{ 'flex items-center space-x-2': showBadge }">
        <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
          <span class="text-xs c-white fw-bold">{{ i + 1 }}</span>
        </div>
      </div>
      <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
        {{ isDigitalProduct ? $t.selectCardAmountError : replaceAll($t.selectSizeError, { label }) }}
      </vf-form-error>
      <fieldset
        v-if="type === 'color' && isFranchiseAttribute(groupBy, groupedOptions)"
        :aria-labelledby="colorId"
        class="min-w-0 space-y-2"
        :data-test-id="`${type}-groups-item`"
      >
        <legend class="sr-only">
          {{ label }}
        </legend>
        <div
          ref="franchiseTabsRef"
          :aria-label="label"
          class="flex gap-1 overflow-x-auto px-4 scrollbar-none -mx-4 lg:mx-0 lg:overflow-visible lg:px-0 lg:wrap"
          data-test-id="franchise-tabs"
          role="tablist"
        >
          <base-button
            v-for="({ groupValue, options }, franchiseIndex) in groupedOptions"
            :key="groupValue?.toString()"
            :aria-selected="isActiveFranchise(groupValue)"
            class="shrink-0 ws-nowrap rounded-xs px-3 py-2 text-xs"
            :class="getFranchiseTabClasses(groupValue, franchiseIndex)"
            :data-test-id="`franchise-${groupValue}`"
            role="tab"
            :tabindex="isActiveFranchise(groupValue) ? 0 : -1"
            @click="selectFranchise(groupValue, options)"
            @keydown.end.prevent="focusFranchiseTab(groupedOptions.length - 1)"
            @keydown.home.prevent="focusFranchiseTab(0)"
            @keydown.left.prevent="focusRelativeFranchiseTab(franchiseIndex, -1)"
            @keydown.right.prevent="focusRelativeFranchiseTab(franchiseIndex, 1)"
            @pointerenter="hoveredFranchiseIndex = franchiseIndex"
            @pointerleave="hoveredFranchiseIndex = null"
          >
            {{ groupValue }}
          </base-button>
        </div>
        <vf-carousel
          :key="activeFranchiseValue"
          class-container="gap-1 p-1 -m-1 <lg:w-screen <lg:scroll-px-4 <lg:px-4 <lg:-mx-4 lg:wrap"
          class-controls="o:hidden"
          disable-focus
          @pointerdown.stop
        >
          <vf-color-picker
            v-for="(colorOption, k) in getVisibleColorOptions(groupBy, groupedOptions)"
            :key="colorOption.id"
            ref="colorOptionsRef"
            :active="!pending && getIsFranchiseColorActive(colorOption.value, colorOption.id)"
            :class="{ 'lg:hidden': colorOptionsShown < (k + 1) }"
            :color="colorOption.swatch?.color"
            :loading="getIsPending(colorOption.value, colorOption.id, pending)"
            :name="colorOption.label"
            :sale="$feature.showOnSaleSwatchIndicatorOnPDP
              && colorOption.price.lowCurrent < colorOption.price.lowOriginal"
            :size="colorPickerSize"
            :thumbnail="colorOption.swatch?.image"
            :unavailable="!isComingSoon && (!colorOption.available && !pending)"
            :variant="colorPickerShape"
            @click="selectColorOption(colorOption, colorOption.label)"
          />
          <base-link
            v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl"
            v-style="customizeStyles"
            class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 body-4 <lg:text-micro"
            :to="product.customsExternalUrl"
            @click="$gtm.push('pdpPage.onCustomize')"
          >
            <vf-icon name="customs" size="md" />
            <span>{{ $t.customize }}</span>
          </base-link>
          <base-button
            v-if="colorOptionsShown < getVisibleColorOptions(groupBy, groupedOptions).length"
            ref="showMoreColorsRef"
            v-style="container"
            class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
            type="button"
            @click="showMoreColors"
          >
            <span class="subtitle-4">+{{ getVisibleColorOptions(groupBy, groupedOptions).length - colorOptionsMax }}</span>
            <span class="body-4">{{ $t.more }}</span>
          </base-button>
          <base-button
            v-else-if="colorOptionsExpanded"
            v-style="container"
            class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
            type="button"
            @click="showLessColors"
          >
            <span class="ws-pre text-center body-4">{{ $t.showLess }}</span>
          </base-button>
        </vf-carousel>
        <div class="flex text-xs between">
          <p>
            <span :id="colorId">{{ label }}</span>: {{ activeColorOption?.label || attributeSelectionLabels[type]?.value }}
          </p>
          <p v-if="displayedColorStatus">
            {{ displayedColorStatus }}
          </p>
        </div>
      </fieldset>
      <div
        v-else
        v-for="({ options }, j) in groupedOptions"
        :key="j"
        :data-test-id="`${type}-groups-item`"
      >
        <fieldset v-if="type === 'color'" :aria-labelledby="colorId" class="space-y-2">
          <vf-carousel
            class-container="gap-1 p-1 -m-1 <lg:w-screen <lg:scroll-px-4 <lg:px-4 <lg:-mx-4 lg:wrap"
            class-controls="o:hidden"
            disable-focus
            @pointerdown.stop
          >
            <vf-color-picker
              v-for="({ available, id, label: optionLabel, price, swatch, url, value }, k) in
                options.sort((a, b) => a.id === firstSelectedProduct ? -1 : b.id === firstSelectedProduct ? 1 : 0)"
              :key="optionLabel"
              ref="colorOptionsRef"
              :active="!pending && getIsActive(value, id)"
              :class="{ 'lg:hidden': options.length > colorSwatchesTruncateThreshold && colorOptionsShown < (k + 1) }"
              :color="swatch?.color"
              :loading="getIsPending(value, id, pending)"
              :name="optionLabel"
              :sale="$feature.showOnSaleSwatchIndicatorOnPDP && price.lowCurrent < price.lowOriginal"
              :size="colorPickerSize"
              :thumbnail="swatch?.image"
              :unavailable="!isComingSoon && (!available && !pending)"
              :variant="colorPickerShape"
              @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
            />
            <base-link
              v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl"
              v-style="customizeStyles"
              class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 body-4 <lg:text-micro"
              :to="product.customsExternalUrl"
              @click="$gtm.push('pdpPage.onCustomize')"
            >
              <vf-icon name="customs" size="md" />
              <span>{{ $t.customize }}</span>
            </base-link>
            <base-button
              v-if="options.length > colorSwatchesTruncateThreshold && colorOptionsShown < options.length"
              ref="showMoreColorsRef"
              v-style="container"
              class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
              type="button"
              @click="showMoreColors"
            >
              <span class="subtitle-4">+{{ options.length - colorOptionsMax }}</span>
              <span class="body-4">{{ $t.more }}</span>
            </base-button>
            <base-button
              v-else-if="colorOptionsExpanded"
              v-style="container"
              class="f-col center rounded @hactive:bg-grey-70 bg-brand-2 <lg:hidden"
              type="button"
              @click="showLessColors"
            >
              <span class="ws-pre text-center body-4">{{ $t.showLess }}</span>
            </base-button>
          </vf-carousel>
          <div class="flex items-end gap-6 between">
            <p class="min-w-0 flex-1 text-xs">
              {{ attributeSelectionLabels[type]?.value }}
            </p>
            <product-eyebrow class="shrink-0" :interactive="false" />
          </div>
        </fieldset>
        <fieldset v-else>
          <div class="flex items-center between">
            <legend class="subtitle-5 c-brand-1" :class="{ 'sr-only': !$feature.pdpShowDropdownLabel }">
              {{ label }}
              <span v-if="type === 'size' && product?.sizingStandard">
                ({{ product?.sizingStandard }})
              </span>
              <span v-if="type === 'size' && selectedConvertedSizeLabel">
                ({{ selectedConvertedSizeLabel }})
              </span>
            </legend>
            <div v-if="type === 'size'" class="f-col shrink-0 items-end text-xs">
              <div class="mb-1 underlined">
                <slot name="size-chart" />
              </div>
              <slot name="size-advice" />
            </div>
          </div>
          <base-form-field
            v-slot="{ attrs: { onChange } }"
            :name="type"
            :rule="validateRequired()"
            :selector="`${type}Pickers`"
          >
            <vf-size-select
              v-model="attributeSelection[type]"
              class="mt-2"
              :show-placeholder-stock-hint="showStockStatusLabel"
              :unavailable="!pending && !options.find(({ value }) => value === attributeSelection[type])?.available"
              @change="onChange"
              @click="handleSizeSelect(variantAttributes[i])"
            >
              <template v-if="formatAttributeSelectionLabelAbbreviations(attributeSelectionLabels[type]?.value)">
                <span aria-hidden="true">{{ attributeSelectionLabels[type]?.value }}</span>
                <span class="sr-only">{{ formatAttributeSelectionLabelAbbreviations(attributeSelectionLabels[type]?.value) }}</span>
              </template>
              <template v-else>
                {{ attributeSelectionLabels[type]?.value || $t.selectAttribute[type] }}
              </template>
            </vf-size-select>
          </base-form-field>
        </fieldset>
      </div>
    </section>
    <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
  </component>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { Responsive } from '#types/common'
import type { ColorPickerStyles } from '#types/config/components/vf/colorPicker'
import type {
  AttributeSelection,
  Badge,
  ColorOption,
  ColorSwatchShape,
  OptionGroups,
  ProductAttributeSelectionLabels,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseForm } from '#components'

const {
  attributeSelectionLabels,
  colorSize,
  colorSwatch,
  handleNextAttribute,
  hideStockStatusLabel,
  isComingSoon,
  pending,
  product
} = defineProps<{
  /*
  * variants, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: ProductAttributeSelectionLabels
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  handleNextAttribute?: () => void
  hideStockStatusLabel?: boolean
  isComingSoon?: boolean
  isDigitalProduct?: boolean
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant | null
  showBadge?: boolean
  asForm?: boolean
  variants?: any
}>()

const emit = defineEmits<{
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}>()

defineSlots<{
  'size-chart': () => void
  'size-advice': () => void
}>()

const config = useAppConfig()
const { brandStyles } = config.components.vf.colorPicker
const { enableColorAsAttribute, swatches } = config.pages.pdp
const colorId = useId()
const { $feature, $gtm, $t } = useNuxtApp()
const colorOptionsRef = useTemplateRef('colorOptionsRef')
const franchiseTabsRef = useTemplateRef<HTMLElement[]>('franchiseTabsRef')
const showMoreColorsRef = useTemplateRef('showMoreColorsRef')

const attributeSelection = defineModel<AttributeSelection>({ required: true })
const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })

const PanelVariantSelection = createPanel('variant-selection')

const colorOptionsExpanded = ref(false)
const selectedProductId = ref()
const selectedColorOptionId = ref<string>()
const selectedFranchiseValue = ref<string>()
const hoveredFranchiseIndex = ref<number | null>(null)
const lastSelectedColorByFranchise = reactive(new Map<string, string>())

const colorPickerShape = colorSwatch ?? swatches.variant
const colorPickerSize = colorSize ?? swatches.size

const { container } = Object.entries(brandStyles[colorPickerShape]).reduce((acc: ColorPickerStyles, [key, value]) => {
  acc[key] = {}

  for (const styleProp in value) {
    acc[key][styleProp] = replaceValues(isObject(colorPickerSize)
      ? colorPickerSize
      : { sm: colorPickerSize }, value[styleProp])
  }

  return acc
}, {})

// When the user hits the PDP for the first time, we store the first selected product
// so its color swatch stays first in the list — order shouldn't reshuffle as the user clicks around
const firstSelectedProduct = product.id

const customizeColSpan = $t.customize.length < 10 ? 1 : 2

const customizeStyles = {
  ...container,
  w: {
    sm: container?.w?.sm
      && `${(Number(container.w.sm.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`,
    md: container?.w?.md
      && `${(Number(container.w.md.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`,
    lg: container?.w?.lg
      && `${(Number(container.w.lg.replace('rem', '')) * customizeColSpan + 0.25 * (customizeColSpan - 1))}rem`
  }
}

// Max tiles visible in the swatch row, shared across swatches, Customize, and "+More"
const COLOR_SWATCHES_VISIBLE_BUDGET = 20

const customizeCost = computed(() => (
  $feature.showCustomizeOnPDP && product.customsExternalUrl ? customizeColSpan : 0
))

// Remaining slots for swatches once Customize is accounted for
const colorSwatchesTruncateThreshold = computed(() => COLOR_SWATCHES_VISIBLE_BUDGET - customizeCost.value)

// Reserve a slot for "+More" whenever swatches are truncated
const colorOptionsMax = computed(() => colorSwatchesTruncateThreshold.value - 1)

const colorOptionsShown = computed(() => colorOptionsExpanded.value
  ? Number.POSITIVE_INFINITY
  : colorOptionsMax.value
)

const variantAttributes = computed(() => isComingSoon
  ? product.attributes.filter((attribute) => attribute.type === 'color')
  : product.attributes
)

const colorAttribute = computed(() => variantAttributes.value.find(({ type }) => type === 'color'))
const activeColorOption = computed(() => colorAttribute.value?.options
  .find(({ id }) => id === product.id) as ColorOption | undefined)

const showStockStatusLabel = computed(() => $feature.showSizeLevelStockMessagingOnPDP && !hideStockStatusLabel)

const getBadgeLabel = (badge?: Badge) => (isArray(badge)
  ? badge.find(({ label }) => label)?.label
  : badge?.label)

const isFranchiseAttribute = (
  groupBy: string | undefined,
  groups: OptionGroups[]
) => product.enableFranchisePDP === true
  && groupBy === 'productSubGroup'
  && groups.length > 0
  && groups.every(({ groupValue }) => isString(groupValue) && groupValue.length > 0)

const displayedColorStatus = computed(() => {
  const eyebrow = activeColorOption.value?.eyebrow ?? product.eyebrow
  const badge = activeColorOption.value?.badge ?? product.badge
  const available = activeColorOption.value?.available

  return eyebrow?.label
    || getBadgeLabel(badge)
    || (available === false ? $t.outOfStock : undefined)
})

const selectedColorId = computed(() => selectedColorOptionId.value || product.id)

const getActiveFranchise = (groups: OptionGroups[]) => {
  const selectedGroup = groups.find(({ groupValue, options }) => groupValue === selectedFranchiseValue.value
    && options.some(({ id }) => id === selectedColorId.value))
  if (selectedGroup) return selectedGroup

  const productSubGroup = colorAttribute.value?.options.find(({ id }) => id === selectedColorId.value)?.productSubGroup
  const firstSubGroup = isArray(productSubGroup)
    ? productSubGroup.find((group) => group.trim())?.trim()
    : productSubGroup?.trim()

  return groups.find(({ groupValue }) => groupValue === firstSubGroup)
    || groups.find(({ options }) => options.some(({ id }) => id === selectedColorId.value))
    || groups[0]
}

const activeFranchise = computed(() => colorAttribute.value
  && getActiveFranchise(colorAttribute.value.groupedOptions)
)

const activeFranchiseValue = computed(() => activeFranchise.value?.groupValue?.toString())

const getVisibleColorOptions = (groupBy: string | undefined, groups: OptionGroups[]) =>
  isFranchiseAttribute(groupBy, groups)
    ? getActiveFranchise(groups)?.options || []
    : groups.flatMap(({ options }) => options)

const isActiveFranchise = (groupValue: OptionGroups['groupValue']) => groupValue === activeFranchise.value?.groupValue

const getFranchiseTabClasses = (groupValue: OptionGroups['groupValue'], index: number) => {
  const active = isActiveFranchise(groupValue)
  const hovered = hoveredFranchiseIndex.value === index

  return {
    'bg-grey-10 c-white': active,
    'bg-grey-70': !active && hovered,
    'bg-grey-80 c-grey-20': !active && !hovered
  }
}

const selectedConvertedSizeLabel = computed(() => {
  if (!attributeSelection.value?.size || !product?.variants) return

  const matchedVariant = product.variants.find(
    (variant) => variant.attributes?.size === attributeSelection.value.size
  )

  return matchedVariant?.convertedSizeLabel
})

const getIsActive = (value: string, id: string) => enableColorAsAttribute
  ? attributeSelection.value.color === value
  : product.id === id

const getIsFranchiseColorActive = (value: string, id: string) => enableColorAsAttribute
  ? attributeSelection.value.color === value
  : selectedColorId.value === id

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return
  return enableColorAsAttribute
    ? attributeSelection.value.color === value
    : selectedProductId.value === id
}

const selectNewColor = (id, url, optionLabel?) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

const rememberFranchiseSelection = (optionId: string) => {
  const group = activeFranchise.value

  if (isString(group?.groupValue)) lastSelectedColorByFranchise.set(group.groupValue, optionId)
}

const selectColorOption = (option: ColorOption, optionLabel?: string) => {
  selectedColorOptionId.value = option.id
  rememberFranchiseSelection(option.id)
  selectNewColor(enableColorAsAttribute ? option.value : option.id, option.url, optionLabel)
}

const selectFranchise = (groupValue: OptionGroups['groupValue'], options: ColorOption[]) => {
  if (isActiveFranchise(groupValue)) return

  colorOptionsExpanded.value = false
  const rememberedOptionId = isString(groupValue) && lastSelectedColorByFranchise.get(groupValue)
  const option = options.find(({ id }) => id === rememberedOptionId) || options[0]

  if (option) {
    if (isString(groupValue)) selectedFranchiseValue.value = groupValue
    if (isString(groupValue)) $gtm.push('pdpPage.onSelectStyle', `${option.id.slice(0, 8)} - ${groupValue}`)
    selectColorOption(option)
  }
}

const getFranchiseTabsContainer = () => franchiseTabsRef.value?.[0]

const getFranchiseTabs = () => Array.from(
  getFranchiseTabsContainer()?.querySelectorAll<HTMLElement>('[role="tab"]') || []
)

const focusFranchiseTab = (index: number) => {
  getFranchiseTabs()[index]?.focus()
}

const focusRelativeFranchiseTab = (index: number, offset: number) => {
  const tabs = getFranchiseTabs()
  if (!tabs.length) return

  focusFranchiseTab((index + offset + tabs.length) % tabs.length)
}

const scrollSelectedFranchiseIntoView = async () => {
  await nextTick()

  const tabs = getFranchiseTabsContainer()
  if (!tabs || tabs.scrollWidth <= tabs.clientWidth) return

  tabs.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

const showLessColors = () => {
  colorOptionsExpanded.value = false
  nextTick(() => showMoreColorsRef.value?.[0]?.$el.focus())
}

const showMoreColors = () => {
  colorOptionsExpanded.value = true
  nextTick(() => colorOptionsRef.value?.[colorOptionsMax.value]?.$el.focus())
}

const handleSizeSelect = async (attribute) => {
  PanelVariantSelection.open({ attribute }).then((payload) => {
    const selectedOption = attribute.options.find(({ value }) => value === attributeSelection.value[attribute.type])
    if (selectedOption) emit('selectSize', attribute.type, selectedOption.value, selectedOption.label)
    if (payload?.handleNextAttribute) handleNextAttribute?.()
  })
}

watch(() => product.id, (id) => {
  selectedColorOptionId.value = undefined
  selectedFranchiseValue.value = activeFranchiseValue.value
  rememberFranchiseSelection(id)
}, { immediate: true })

watch(() => pending, (isPending) => {
  if (!isPending && selectedColorOptionId.value !== product.id)
    selectedColorOptionId.value = undefined
})

watch([selectedColorId, activeFranchise, colorOptionsMax], () => {
  const attribute = colorAttribute.value
  if (!attribute || !isFranchiseAttribute(attribute.groupBy, attribute.groupedOptions)) return

  const selectedIndex = activeFranchise.value?.options.findIndex(({ id }) => id === selectedColorId.value) ?? -1
  if (selectedIndex >= colorOptionsMax.value) colorOptionsExpanded.value = true
}, { immediate: true })

watch(activeFranchiseValue, scrollSelectedFranchiseIntoView, { flush: 'post', immediate: true })

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
